/** Session-scoped last tagged email touch, never a claim of causal conversion. */
export type EmailAttribution = {
  utm_source: "lifecycle_email";
  utm_medium: "email";
  utm_campaign: string;
  utm_content: string;
};

const STORAGE_KEY = "sehaj:email-attribution:v1";
const SLUG = /^[a-z0-9][a-z0-9_-]{0,63}$/;

const normalize = (value: unknown): EmailAttribution | undefined => {
  if (!value || typeof value !== "object") return;
  const candidate = value as Record<string, unknown>;
  if (candidate.utm_source !== "lifecycle_email" || candidate.utm_medium !== "email") return;
  if (typeof candidate.utm_campaign !== "string" || !SLUG.test(candidate.utm_campaign)) return;
  const content = candidate.utm_content ?? "legacy";
  if (typeof content !== "string" || !SLUG.test(content)) return;
  return { utm_source: "lifecycle_email", utm_medium: "email",
    utm_campaign: candidate.utm_campaign, utm_content: content };
};

export const resolveEmailAttribution = (
  url: URL,
  storage?: Pick<Storage, "getItem" | "setItem" | "removeItem">
): EmailAttribution | undefined => {
  const tagged = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "gclid", "fbclid"]
    .some((key) => url.searchParams.has(key));
  if (tagged) {
    const current = normalize(Object.fromEntries(url.searchParams));
    try {
      if (current) storage?.setItem(STORAGE_KEY, JSON.stringify(current));
      else storage?.removeItem(STORAGE_KEY);
    } catch { /* Storage restrictions must never block checkout. */ }
    return current;
  }
  try { return normalize(JSON.parse(storage?.getItem(STORAGE_KEY) ?? "null")); }
  catch { return undefined; }
};

export const getEmailAttribution = (): EmailAttribution | undefined => {
  if (typeof window === "undefined") return;
  let storage: Storage | undefined;
  try { storage = window.sessionStorage; } catch { /* Private browsing. */ }
  return resolveEmailAttribution(new URL(window.location.href), storage);
};
